import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { sanitizeFormInput } from "@/utils/sanitize";
import { useRateLimit } from "@/hooks/useRateLimit";

export const useUserExperiences = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { checkRateLimit } = useRateLimit();

  const submitExperience = async (tipId: string, experienceText: string, rating: number) => {
    try {
      setIsLoading(true);

      // Rate limiting check
      const rateLimitResult = await checkRateLimit('submit_experience', 3, 60); // 3 submissions per hour
      if (!rateLimitResult.allowed) {
        const resetTime = rateLimitResult.resetTime ? rateLimitResult.resetTime.toLocaleTimeString() : 'later';
        toast({
          title: "Too many submissions",
          description: `Please wait until ${resetTime} before submitting another experience.`,
          variant: "destructive",
        });
        return { success: false, error: 'Rate limit exceeded' };
      }

      // Sanitize input
      const sanitizedText = sanitizeFormInput(experienceText, 1000);
      
      // Validate input before submission
      const { data: validationResult, error: validationError } = await supabase
        .rpc('validate_user_experience', {
          experience_text: experienceText,
          rating: rating
        });

      if (validationError) {
        console.error("Validation error:", validationError);
        toast({
          title: "Validation Error",
          description: "Please check your input and try again.",
          variant: "destructive",
        });
        return { success: false, error: validationError };
      }

      if (!validationResult) {
        toast({
          title: "Invalid Input",
          description: "Experience must be 10-1000 characters, rating 1-5, and contain appropriate content.",
          variant: "destructive",
        });
        return { success: false, error: new Error("Input validation failed") };
      }
      
      const { data, error } = await supabase
        .from("user_experiences")
        .insert({
          tip_id: tipId,
          experience_text: experienceText,
          rating: rating,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Experience shared!",
        description: "Thank you for sharing your experience. Your rating has been added to help others.",
      });

      // The trigger will automatically update the tip rating
      // No need to manually refresh since real-time updates will handle it
      return { success: true, data };
    } catch (error) {
      console.error("Error submitting experience:", error);
      toast({
        title: "Error",
        description: "Failed to share your experience. Please try again.",
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const getExperiencesForTip = async (tipId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_experiences")
        .select("*")
        .eq("tip_id", tipId)
        .eq("is_approved", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error fetching experiences:", error);
      return { success: false, error };
    }
  };

  const getAllExperiences = async () => {
    try {
      const { data, error } = await supabase
        .from("user_experiences")
        .select(`
          *,
          tips (
            id,
            title,
            category,
            published_at
          )
        `)
        .eq("is_approved", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error fetching all experiences:", error);
      return { success: false, error };
    }
  };

  return {
    submitExperience,
    getExperiencesForTip,
    getAllExperiences,
    isLoading,
  };
};